import { ClientPage } from "../../submodules/rapdv/client/elements/PagesCtrl"
import { Modal } from "../../submodules/rapdv/client/elements/Bootstrap"
import { Translation } from "../elements/Translation"

type DeliveryArea = {
  name: string
  deliveryCost: number
  polygon: number[][]
}

export class PanelDeliveryOptions implements ClientPage {

  private static readonly TRANSLATION_ID = "PanelDeliveryOptions"
  private static readonly DEFAULT_MAP_STYLE_URL = "https://tiles.openfreemap.org/styles/liberty"
  private static readonly AREA_COLORS = ["#f26125", "#1f7fb8", "#1c7e1f", "#c8a24a", "#6f42c1", "#e83e8c"]
  private static readonly RESTAURANT_MARKER_COLOR = "#f26125"
  private static readonly AREA_FILL_OPACITY = 0.18
  private static readonly MIN_POLYGON_POINTS = 3
  private static readonly CLOSE_POLYGON_THRESHOLD_PX = 14
  private static readonly AREA_ZOOM = 12
  private static readonly WORLD_ZOOM = 1
  private static readonly FIT_BOUNDS_PADDING_PX = 40

  private deliveryToDoors: HTMLInputElement | null = null
  private deliveryToTable: HTMLInputElement | null = null
  private eatInPlacesSection: HTMLElement | null = null
  private areasSection: HTMLElement | null = null
  private areasInput: HTMLInputElement | null = null
  private areasListElement: HTMLElement | null = null
  private areasEmptyElement: HTMLElement | null = null
  private areas: DeliveryArea[] = []
  private currency: string = ""

  private areaModal: Modal | null = null
  private map: any = null
  private maplibre: any = null
  private restaurantLocation: number[] | null = null
  private editIndex: number = -1
  private isDrawing: boolean = false
  private draftPoints: number[][] = []
  private polygonPoints: number[][] = []

  private translation = new Translation()
  private t = (key: string): string => this.translation.t(key)

  getPageId = (): string => "delivery-options-panel"

  execute = async (): Promise<void> => {
    this.deliveryToDoors = document.getElementById("deliveryToDoors") as HTMLInputElement | null
    this.deliveryToTable = document.getElementById("deliveryToTable") as HTMLInputElement | null
    this.eatInPlacesSection = document.querySelector("[data-eatin-places-section]")
    this.areasSection = document.querySelector("[data-delivery-areas-section]")
    this.areasInput = document.querySelector("[data-delivery-areas-input]")
    this.areasListElement = document.querySelector("[data-delivery-areas-list]")
    this.areasEmptyElement = document.querySelector("[data-delivery-areas-empty]")
    this.currency = this.areasSection?.dataset.currency ?? ""

    this.deliveryToDoors?.addEventListener("change", this.toggleAreasSection)
    this.deliveryToTable?.addEventListener("change", this.toggleEatInPlacesSection)

    if (!this.areasSection) return

    await this.translation.fetchTranslations(PanelDeliveryOptions.TRANSLATION_ID)

    try {
      this.areas = JSON.parse(this.areasInput?.value || "[]")
    } catch (error) {
      this.areas = []
    }

    this.setupModal()
    this.renderAreas()
  }

  onPageClose = (): void => {
    this.map?.remove()
    this.map = null
    this.areaModal = null
    this.restaurantLocation = null
  }

  private toggleAreasSection = (): void => {
    const isDeliveryEnabled = !!this.deliveryToDoors?.checked
    this.areasSection?.classList.toggle("d-none", !isDeliveryEnabled)
  }

  private toggleEatInPlacesSection = (): void => {
    const isEatInEnabled = !!this.deliveryToTable?.checked
    this.eatInPlacesSection?.classList.toggle("d-none", !isEatInEnabled)
  }

  private syncAreasInput = (): void => {
    if (this.areasInput) this.areasInput.value = JSON.stringify(this.areas)
  }

  private getAreaColor = (index: number): string =>
    PanelDeliveryOptions.AREA_COLORS[index % PanelDeliveryOptions.AREA_COLORS.length]

  private escapeHtml = (text: string): string => {
    const helperElement = document.createElement("div")
    helperElement.textContent = text
    return helperElement.innerHTML
  }

  private renderAreas = (): void => {
    if (!this.areasListElement) return

    this.areasEmptyElement?.classList.toggle("d-none", this.areas.length > 0)

    const rows = this.areas.map((area, index) => {
      const color = this.getAreaColor(index)
      const name = area.name?.trim() ? this.escapeHtml(area.name) : `${this.t("Delivery area")} ${index + 1}`
      const cost = `${(Number(area.deliveryCost) || 0).toFixed(2)} ${this.currency}`
      return `
        <div class="d-flex align-items-center gap-2 border rounded p-2 mb-2">
          <span class="delivery-area-swatch" style="background:${color}33;border-color:${color}"></span>
          <div class="flex-grow-1">
            <div class="fw-semibold">${name}</div>
            <div class="text-muted small"><i class="bi bi-pentagon me-1"></i>${this.t("Zone mapped")}</div>
          </div>
          <div class="fw-semibold text-nowrap">${cost}</div>
          <button type="button" class="btn btn-outline-secondary btn-sm" data-area-edit="${index}" title="${this.t("Edit area")}" aria-label="${this.t("Edit area")}">
            <i class="bi bi-pencil"></i>
          </button>
          <button type="button" class="btn btn-outline-danger btn-sm" data-area-delete="${index}" title="${this.t("Remove area")}" aria-label="${this.t("Remove area")}">
            <i class="bi bi-trash3"></i>
          </button>
        </div>
      `
    })
    this.areasListElement.innerHTML = rows.join("")

    for (const button of this.areasListElement.querySelectorAll("[data-area-edit]")) {
      button.addEventListener("click", () => this.openAreaModal(Number((button as HTMLElement).dataset.areaEdit)))
    }
    for (const button of this.areasListElement.querySelectorAll("[data-area-delete]")) {
      button.addEventListener("click", () => {
        this.areas.splice(Number((button as HTMLElement).dataset.areaDelete), 1)
        this.syncAreasInput()
        this.renderAreas()
      })
    }
  }

  private setupModal = (): void => {
    const modalElement = document.getElementById("delivery-area-modal")
    if (!modalElement) return

    this.areaModal = new Modal(modalElement)
    modalElement.addEventListener("shown.bs.modal", () => this.map?.resize())

    document.querySelector("[data-delivery-area-add]")?.addEventListener("click", () => this.openAreaModal(-1))
    document.querySelector("[data-delivery-area-draw]")?.addEventListener("click", this.startDrawing)
    document.querySelector("[data-delivery-area-save]")?.addEventListener("click", this.saveArea)
  }

  private openAreaModal = async (index: number): Promise<void> => {
    this.editIndex = index
    const isEditing = index > -1
    const area = isEditing ? this.areas[index] : null

    const titleElement = document.querySelector("[data-delivery-area-modal-title]")
    if (titleElement) titleElement.textContent = isEditing ? this.t("Edit delivery area") : this.t("Add delivery area")

    const nameInput = document.querySelector("[data-delivery-area-name]") as HTMLInputElement | null
    const costInput = document.querySelector("[data-delivery-area-cost]") as HTMLInputElement | null
    if (nameInput) nameInput.value = area?.name ?? ""
    if (costInput) costInput.value = area ? String(area.deliveryCost) : ""

    this.isDrawing = false
    this.draftPoints = []
    this.polygonPoints = (area?.polygon ?? []).map(([lat, lng]) => [lng, lat])

    this.areaModal?.show()
    await this.initializeMap()
    this.applyAreaColorToMap(this.getAreaColor(isEditing ? index : this.areas.length))
    this.updateMapSources()
    this.updateDrawState()
    if (isEditing) {
      this.focusPolygon()
    } else {
      this.focusRestaurant()
      if (this.map) this.startDrawing()
    }
  }

  private initializeMap = async (): Promise<void> => {
    if (this.map) return

    const mapContainer = document.querySelector("[data-delivery-area-map]") as HTMLElement | null
    if (!mapContainer) return

    const maplibreModule: any = await import("maplibre-gl")
    this.maplibre = maplibreModule.default ?? maplibreModule

    const centerLatitude = parseFloat(this.areasSection?.dataset.centerLatitude ?? "")
    const centerLongitude = parseFloat(this.areasSection?.dataset.centerLongitude ?? "")
    const hasCenter = Number.isFinite(centerLatitude) && Number.isFinite(centerLongitude)

    this.map = new this.maplibre.Map({
      container: mapContainer,
      style: mapContainer.dataset.mapStyle || PanelDeliveryOptions.DEFAULT_MAP_STYLE_URL,
      center: hasCenter ? [centerLongitude, centerLatitude] : [0, 20],
      zoom: hasCenter ? PanelDeliveryOptions.AREA_ZOOM : PanelDeliveryOptions.WORLD_ZOOM,
    })
    this.map.addControl(new this.maplibre.NavigationControl())

    if (hasCenter) {
      this.restaurantLocation = [centerLongitude, centerLatitude]
      new this.maplibre.Marker({ color: PanelDeliveryOptions.RESTAURANT_MARKER_COLOR })
        .setLngLat(this.restaurantLocation)
        .addTo(this.map)
    }

    await new Promise((resolve) => this.map.on("load", resolve))

    const emptyGeoJson = { type: "FeatureCollection", features: [] }
    this.map.addSource("delivery-area", { type: "geojson", data: emptyGeoJson })
    this.map.addSource("delivery-area-draft", { type: "geojson", data: emptyGeoJson })

    this.map.addLayer({
      id: "delivery-area-fill",
      type: "fill",
      source: "delivery-area",
      paint: { "fill-color": PanelDeliveryOptions.AREA_COLORS[0], "fill-opacity": PanelDeliveryOptions.AREA_FILL_OPACITY },
    })
    this.map.addLayer({
      id: "delivery-area-outline",
      type: "line",
      source: "delivery-area",
      paint: { "line-color": PanelDeliveryOptions.AREA_COLORS[0], "line-width": 2 },
    })
    this.map.addLayer({
      id: "delivery-area-draft-line",
      type: "line",
      source: "delivery-area-draft",
      filter: ["==", "$type", "LineString"],
      paint: { "line-color": PanelDeliveryOptions.AREA_COLORS[0], "line-width": 2, "line-dasharray": [2, 1] },
    })
    this.map.addLayer({
      id: "delivery-area-draft-points",
      type: "circle",
      source: "delivery-area-draft",
      filter: ["==", "$type", "Point"],
      paint: { "circle-radius": 5, "circle-color": "#ffffff", "circle-stroke-color": PanelDeliveryOptions.AREA_COLORS[0], "circle-stroke-width": 2 },
    })

    this.map.on("click", this.onMapClick)
    this.map.resize()
  }

  private onMapClick = (event: any): void => {
    if (!this.isDrawing) return

    const clickedPoint: number[] = [event.lngLat.lng, event.lngLat.lat]

    if (this.draftPoints.length >= PanelDeliveryOptions.MIN_POLYGON_POINTS) {
      const firstPointPixels = this.map.project(this.draftPoints[0])
      const distancePixels = Math.hypot(firstPointPixels.x - event.point.x, firstPointPixels.y - event.point.y)
      if (distancePixels <= PanelDeliveryOptions.CLOSE_POLYGON_THRESHOLD_PX) {
        this.finishDrawing()
        return
      }
    }

    this.draftPoints.push(clickedPoint)
    this.updateMapSources()
  }

  private startDrawing = (): void => {
    this.isDrawing = true
    this.draftPoints = []
    this.polygonPoints = []
    this.map.getCanvas().style.cursor = "crosshair"
    document.querySelector("[data-delivery-area-hint]")?.classList.remove("d-none")
    this.updateMapSources()
    this.updateDrawState()
  }

  private finishDrawing = (): void => {
    this.polygonPoints = this.draftPoints
    this.draftPoints = []
    this.isDrawing = false
    this.map.getCanvas().style.cursor = ""
    document.querySelector("[data-delivery-area-hint]")?.classList.add("d-none")
    this.updateMapSources()
    this.updateDrawState()
  }

  private updateMapSources = (): void => {
    if (!this.map?.getSource("delivery-area")) return

    const polygonFeatures = this.polygonPoints.length >= PanelDeliveryOptions.MIN_POLYGON_POINTS
      ? [{ type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [[...this.polygonPoints, this.polygonPoints[0]]] } }]
      : []
    this.map.getSource("delivery-area").setData({ type: "FeatureCollection", features: polygonFeatures })

    const draftFeatures: any[] = this.draftPoints.map((point) => ({
      type: "Feature", properties: {}, geometry: { type: "Point", coordinates: point },
    }))
    if (this.draftPoints.length >= 2) {
      draftFeatures.push({ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: this.draftPoints } })
    }
    this.map.getSource("delivery-area-draft").setData({ type: "FeatureCollection", features: draftFeatures })
  }

  private focusPolygon = (): void => {
    if (!this.map || this.polygonPoints.length < PanelDeliveryOptions.MIN_POLYGON_POINTS) return

    const bounds = new this.maplibre.LngLatBounds(this.polygonPoints[0], this.polygonPoints[0])
    for (const point of this.polygonPoints) bounds.extend(point)
    this.map.fitBounds(bounds, { padding: PanelDeliveryOptions.FIT_BOUNDS_PADDING_PX, animate: false })
  }

  private focusRestaurant = (): void => {
    if (!this.map || !this.restaurantLocation) return
    this.map.jumpTo({ center: this.restaurantLocation, zoom: PanelDeliveryOptions.AREA_ZOOM })
  }

  private applyAreaColorToMap = (color: string): void => {
    if (!this.map) return
    this.map.setPaintProperty("delivery-area-fill", "fill-color", color)
    this.map.setPaintProperty("delivery-area-outline", "line-color", color)
    this.map.setPaintProperty("delivery-area-draft-line", "line-color", color)
    this.map.setPaintProperty("delivery-area-draft-points", "circle-stroke-color", color)
  }

  private updateDrawState = (): void => {
    const stateElement = document.querySelector("[data-delivery-area-state]")
    const saveButton = document.querySelector("[data-delivery-area-save]") as HTMLButtonElement | null
    const hasPolygon = this.polygonPoints.length >= PanelDeliveryOptions.MIN_POLYGON_POINTS

    if (stateElement) {
      stateElement.innerHTML = hasPolygon
        ? `<i class="bi bi-check-circle-fill text-success me-1"></i>${this.t("Area drawn")}`
        : `<i class="bi bi-info-circle me-1"></i>${this.t("Draw an area to continue")}`
    }
    if (saveButton) saveButton.disabled = !hasPolygon
  }

  private saveArea = (): void => {
    if (this.polygonPoints.length < PanelDeliveryOptions.MIN_POLYGON_POINTS) return

    const nameInput = document.querySelector("[data-delivery-area-name]") as HTMLInputElement | null
    const costInput = document.querySelector("[data-delivery-area-cost]") as HTMLInputElement | null

    const area: DeliveryArea = {
      name: nameInput?.value?.trim() ?? "",
      deliveryCost: Math.max(0, parseFloat(costInput?.value ?? "") || 0),
      polygon: this.polygonPoints.map(([lng, lat]) => [lat, lng]),
    }

    if (this.editIndex > -1) {
      this.areas[this.editIndex] = area
    } else {
      this.areas.push(area)
    }

    this.syncAreasInput()
    this.renderAreas()
    this.areaModal?.hide()
  }
}
